import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

export interface BoothConfig {
  name: string;
  slug: string;
  description: string;
  imageUrl?: string;
  dimensions?: {
    height: number;
    width: number;
    depth: number;
  };
  customPricing?: {
    enabled: boolean;
    hourlyPackages?: HourlyPackage[];
    ticketSystem?: TicketSystemConfig;
  };
}

export interface HourlyPackage {
  hours: number;
  price: number;
  originalPrice?: number;
  discount?: number;
  enabled: boolean;
}

export interface TicketSystemConfig {
  installationFee: number;
  originalInstallationFee?: number;
  installationDiscount?: number;
  freeTickets: number;
  ticketPrice: number;
  originalTicketPrice?: number;
  ticketDiscount?: number;
  enabled: boolean;
}

export interface SystemFeatures {
  hourlyFeatures: string[];
  ticketFeatures: string[];
}

export interface ContactInfo {
  email: string;
  whatsapp: string;
  instagram: string;
}

export interface AppConfig {
  booths: BoothConfig[];
  hourlySystemEnabled: boolean;
  hourlyPackages: HourlyPackage[];
  ticketSystemEnabled: boolean;
  ticketSystem: TicketSystemConfig;
  features: SystemFeatures;
  contactInfo: ContactInfo;
  adminNotificationEmails?: string[];
  depositSettings?: {
    enabled: boolean;
    type: 'percentage' | 'fixed';
    percentage: number;
    amount: number;
    autoReminder: boolean;
  };
}

const DEFAULT_CONFIG: AppConfig = {
  booths: [
    {
      name: 'كابينة Classic',
      slug: 'classic',
      description: 'كابينة التصوير الكلاسيكية المثالية للمناسبات الراقية',
      dimensions: {
        height: 200,
        width: 120,
        depth: 150
      }
    },
    {
      name: 'كابينة Vintage',
      slug: 'vintage',
      description: 'كابينة بطابع الثمانينيات مع أحدث التقنيات للحصول على صور مذهلة',
      dimensions: {
        height: 220,
        width: 140,
        depth: 160
      }
    },
    {
      name: 'كابينة 3',
      slug: 'gala',
      description: 'كابينة مخصصة جديدة لتجربة فريدة',
      imageUrl: '/gala-booth.png',
      dimensions: {
        height: 200,
        width: 150,
        depth: 150
      },
      customPricing: {
        enabled: false,
        hourlyPackages: [],
        ticketSystem: {
          enabled: true,
          installationFee: 2500,
          freeTickets: 15,
          ticketPrice: 35
        }
      }
    }
  ],
  hourlySystemEnabled: true,
  hourlyPackages: [
    { hours: 2, price: 3600, enabled: true },
    { hours: 3, price: 3999, enabled: true },
    { hours: 4, price: 4399, enabled: true }
  ],
  ticketSystemEnabled: true,
  ticketSystem: {
    installationFee: 2500,
    freeTickets: 15,
    ticketPrice: 35,
    enabled: true
  },
  features: {
    hourlyFeatures: [
      'طباعة فورية غير محدودة',
      'صور مفتوحة',
      'دعم فني طوال الفترة',
      'خلفيات متعددة'
    ],
    ticketFeatures: [
      'تصميم تذاكر خاص بمناسبتك',
      '15 تذكرة مجانية مع التركيب',
      'نظام باركود للتذاكر',
      'إمكانية شراء تذاكر إضافية'
    ]
  },
  contactInfo: {
    email: 'fotomista.sa@gmail.com',
    whatsapp: '+966503859150',
    instagram: '@fotomista_sa'
  },
  depositSettings: {
    enabled: false,
    type: 'percentage',
    percentage: 50,
    amount: 100,
    autoReminder: true
  },
  adminNotificationEmails: []
};

class ConfigService {
  private config: AppConfig;
  private readonly CONFIG_KEY = 'fotomista_config';
  private readonly CACHE_KEY = 'fotomista_config_cache';

  private initialized = false;
  private maxRetries = 5;
  private retryDelay = 2000; // 2 seconds

  constructor() {
    // بدء بالقيم المحفوظة محلياً أو الافتراضية
    this.config = this.loadFromCache() || DEFAULT_CONFIG;
    // Load complete configuration from API on initialization
    this.initializeConfig();
  }
  
  isInitialized(): boolean {
    return this.initialized;
  }

  // تحميل البيانات من التخزين المحلي
  private loadFromCache(): AppConfig | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        const parsedConfig = JSON.parse(cached);
        console.log('📦 Loaded configuration from cache');
        return parsedConfig;
      }
    } catch (error) {
      console.warn('⚠️ Failed to load from cache:', error);
    }
    return null;
  }

  // حفظ البيانات في التخزين المحلي
  private saveToCache(config: AppConfig): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(config));
      console.log('💾 Configuration saved to cache');
    } catch (error) {
      console.warn('⚠️ Failed to save to cache:', error);
    }
  }

  private async initializeConfig(): Promise<void> {
    await this.loadConfigFromAPI();
  }

  // Public method to initialize config
  async initialize(): Promise<void> {
    await this.loadConfigFromAPI();
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async loadConfigFromAPI(): Promise<void> {
    let retryCount = 0;
    
    while (retryCount < this.maxRetries) {
      try {
        console.log(`🔄 Attempting to load config from API (attempt ${retryCount + 1}/${this.maxRetries})`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONFIG}`, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.config && data.config.booths) {
          console.log('✅ Successfully loaded configuration from database');
          this.config = data.config;
          this.saveToCache(this.config); // حفظ في التخزين المحلي
          this.initialized = true;
          return;
        } else {
          throw new Error('Invalid config structure received from API');
        }
      } catch (error) {
        retryCount++;
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`❌ Attempt ${retryCount} failed:`, errorMessage);
        
        if (retryCount >= this.maxRetries) {
          console.warn('⚠️ All retry attempts failed. Using cached or default configuration');
          // استخدام البيانات المحفوظة أو الافتراضية
          const cachedConfig = this.loadFromCache();
          if (cachedConfig) {
            this.config = cachedConfig;
            console.log('📦 Using cached configuration as fallback');
          } else {
            this.config = DEFAULT_CONFIG;
            console.log('🔄 Using default configuration as final fallback');
          }
          break;
        } else {
          console.log(`⏳ Waiting ${this.retryDelay}ms before retry...`);
          await this.sleep(this.retryDelay);
          // زيادة فترة الانتظار للمحاولات التالية
          this.retryDelay = Math.min(this.retryDelay * 1.5, 10000);
        }
      }
    }
    
    this.initialized = true;
  }


  async saveConfig(config: AppConfig): Promise<void> {
    try {
      const token = localStorage.getItem('fotomista_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONFIG}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ config })
      });
      
      if (response.ok) {
        const data = await response.json();
        this.config = data.config;
        this.saveToCache(this.config); // حفظ في التخزين المحلي أيضاً
        console.log('✅ Complete configuration saved to database successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('❌ Error saving configuration to database:', error);
      throw error;
    }
  }

  async reloadConfig(): Promise<void> {
    console.log('🔄 Manually reloading configuration...');
    await this.loadConfigFromAPI();
  }

  // Force refresh configuration from database
  async refreshConfig(): Promise<void> {
    console.log('🔄 Force refreshing configuration from database...');
    this.retryDelay = 2000; // إعادة تعيين فترة الانتظار
    await this.loadConfigFromAPI();
  }

  getConfig(): AppConfig {
    return this.config;
  }

  async resetToDefault(): Promise<void> {
    try {
      const token = localStorage.getItem('fotomista_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONFIG}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        this.config = data.config;
        console.log('✅ Configuration reset to defaults successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset configuration');
      }
    } catch (error) {
      console.error('❌ Error resetting configuration:', error);
      // Fallback to local reset
      this.config = DEFAULT_CONFIG;
      throw error;
    }
  }

  // Helper methods
  getBooth(slug: string): BoothConfig | undefined {
    return this.config.booths.find(booth => booth.slug === slug);
  }

  getBooths(): BoothConfig[] {
    return this.config.booths;
  }

  getHourlyPackages(boothSlug?: string): HourlyPackage[] {
    if (boothSlug) {
      const booth = this.config.booths.find(b => b.slug === boothSlug);
      if (booth?.customPricing?.enabled && booth.customPricing.hourlyPackages) {
        return booth.customPricing.hourlyPackages.filter(pkg => pkg.enabled);
      }
    }
    return this.config.hourlyPackages.filter(pkg => pkg.enabled);
  }

  isHourlySystemEnabled(): boolean {
    return this.config.hourlySystemEnabled;
  }

  isTicketSystemEnabled(): boolean {
    return this.config.ticketSystemEnabled;
  }

  getTicketSystemConfig(boothSlug?: string): TicketSystemConfig {
    if (boothSlug) {
      const booth = this.config.booths.find(b => b.slug === boothSlug);
      if (booth?.customPricing?.enabled && booth.customPricing.ticketSystem) {
        return booth.customPricing.ticketSystem;
      }
    }
    return this.config.ticketSystem;
  }

  // Calculate ticket system price
  calculateTicketSystemPrice(additionalTickets: number, boothSlug?: string): number {
    const ticketSys = this.getTicketSystemConfig(boothSlug);
    const { installationFee, ticketPrice } = ticketSys;
    
    let total = installationFee;
    
    if (additionalTickets > 0) {
      total += (additionalTickets * ticketPrice);
    }
    
    return total;
  }

  // Admin notification emails management
  getAdminNotificationEmails(): string[] {
    return this.config.adminNotificationEmails || [];
  }

  async updateAdminNotificationEmails(emails: string[]): Promise<void> {
    try {
      const token = localStorage.getItem('fotomista_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONFIG}/admin-emails`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ adminNotificationEmails: emails })
      });

      if (response.ok) {
        this.config.adminNotificationEmails = emails;
        this.saveToCache(this.config);
        console.log('✅ Admin notification emails updated successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update admin notification emails');
      }
    } catch (error) {
      console.error('❌ Error updating admin notification emails:', error);
      throw error;
    }
  }

  async sendTestAdminNotification(): Promise<void> {
    try {
      const token = localStorage.getItem('fotomista_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONFIG}/test-admin-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        console.log('✅ Test admin notification sent successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('❌ Error sending test admin notification:', error);
      throw error;
    }
  }
}

// Create singleton instance
export const configService = new ConfigService();